import { useEffect, useMemo, useState } from 'react'

import { useApi } from '@/hooks'

import { publishLiveRoute } from './liveRouteStore'
import type { RouteHistory, RouteHistoryResponse, RoutePoint } from './types'

export const LIVE_POLL_MS = 30_000
/** Door openings and alerts change far less often than position: every this many polls. */
export const LIVE_EVENTS_EVERY = 10
/** A last fix at least this recent counts as the asset reporting right now. */
export const LIVE_RECENT_MS = 15 * 60_000

const isLiveAt = (to: Date | string, lastFix: Date | string | undefined, now: number): boolean =>
  lastFix !== undefined && new Date(to).getTime() > now && now - new Date(lastFix).getTime() <= LIVE_RECENT_MS

/**
 * Whether a route is worth following live: its range runs past now (today, this week, ...,
 * not a finished period) and its newest fix is recent, so the unit is reporting right now.
 */
export const isLive = (route: RouteHistory, now = Date.now()): boolean =>
  isLiveAt(route.to, route.points.at(-1)?.time, now)

type RouteEvents = Pick<RouteHistory, 'doorOpens' | 'alerts' | 'movements' | 'doors' | 'power'>

/**
 * Follows a route while the asset is reporting. Every LIVE_POLL_MS it asks for the fixes newer
 * than the last one and appends them, so a moving asset's trail grows on the map; every
 * LIVE_EVENTS_EVERY polls it also refreshes the door openings, alerts, movements and power for the
 * whole range and the doors now, which picks up a door that has since closed, an alert that
 * has since cleared or a trip that has since ended. Stops on its
 * own once the range ends or the unit goes quiet; new data (another asset, another range, or
 * the insight's own refresh) starts again from there.
 *
 * `query` is the insight's request query string, which the poll repeats with `since` added.
 * The result is also published under `id` for the card's footer.
 */
export const useLiveRoutePoints = (
  id: string,
  data: RouteHistory,
  query: string
): { route: RouteHistory; live: boolean } => {
  const { api } = useApi()
  const [extra, setExtra] = useState<RoutePoint[]>([])
  const [events, setEvents] = useState<RouteEvents>()
  const [live, setLive] = useState(() => isLive(data))

  const route = useMemo(
    () => (extra.length || events ? { ...data, ...events, points: [...data.points, ...extra] } : data),
    [data, extra, events]
  )

  useEffect(() => {
    publishLiveRoute(id, route === data ? undefined : route)
  }, [id, route, data])
  useEffect(() => () => publishLiveRoute(id, undefined), [id])

  useEffect(() => {
    // Only when there is something to drop: a fresh value would re-render for nothing.
    setExtra((previous) => (previous.length ? [] : previous))
    setEvents(undefined)
    setLive(isLive(data))
    if (!api) return
    let cancelled = false
    // Tracked here rather than read from the rendered route, which can lag a poll behind.
    let newest = data.points.at(-1)?.time
    let polls = 0

    const poll = async () => {
      const stillLive = isLiveAt(data.to, newest, Date.now())
      setLive(stillLive)
      if (!stillLive || newest === undefined) return
      polls += 1
      const withEvents = polls % LIVE_EVENTS_EVERY === 0
      const since = new Date(newest)
      try {
        const update = await api.simpleGet<RouteHistoryResponse>({
          path: `/insights/asset/route-history?${query}&since=${encodeURIComponent(since.toISOString())}${
            withEvents ? '&events=true' : ''
          }`,
          origin: 'RouteHistoryMap#live',
        })
        if (cancelled) return
        const fresh = update.points.filter((p) => new Date(p.time) > since)
        if (fresh.length) {
          newest = fresh[fresh.length - 1].time
          setExtra((previous) => [...previous, ...fresh])
        }
        if (withEvents) {
          setEvents({
            doorOpens: update.doorOpens,
            alerts: update.alerts,
            // Absent from an API that predates them.
            movements: update.movements ?? [],
            doors: update.doors ?? [],
            power: update.power ?? [],
          })
        }
      } catch (error) {
        // A missed poll just means the next one catches up.
        console.error(error)
      }
    }

    const timer = setInterval(() => void poll(), LIVE_POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [api, data, query])

  return { route, live }
}
